/**
 * Lecture Coffre depuis FacturX — mêmes endpoints que Mail / Chat
 * (`coffre-status`, `coffre-list`, `coffre-download`), cookie Suite + X-User-Id.
 */
import { getSuiteApiBase, getSuiteBasePath, readUserId } from "../suiteSession";

export type CoffreZone = "vault" | "ged";

export type CoffreEntry = {
  name: string;
  path: string;
  kind: "folder" | "file";
  size?: number;
};

const LOGO_RE = /\.(jpe?g|png|webp)$/i;

function suiteHeaders(): Headers {
  const headers = new Headers();
  const userId = readUserId();
  if (userId) headers.set("X-User-Id", userId);
  return headers;
}

function suiteUrl(path: string): string {
  return `${getSuiteApiBase()}/${path.replace(/^\//, "")}`;
}

export function isCoffreLogoName(name: string): boolean {
  return LOGO_RE.test(name);
}

export function coffreLogoMime(name: string, type: string): string {
  if (type.startsWith("image/")) return type;
  if (/\.png$/i.test(name)) return "image/png";
  if (/\.webp$/i.test(name)) return "image/webp";
  return "image/jpeg";
}

export function formatCoffreSize(n?: number): string {
  const v = Number(n) || 0;
  if (v < 1) return "";
  if (v < 1024) return `${Math.round(v)} o`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(v >= 10 * 1024 ? 0 : 1)} Ko`;
  return `${(v / (1024 * 1024)).toFixed(1)} Mo`;
}

export function getCoffreAppUrl(): string {
  const base = getSuiteBasePath();
  return `${window.location.origin}${base ? `${base}/` : "/"}coffre/dist/index.html`;
}

export async function fetchCoffreUnlocked(): Promise<{ ok: boolean; unlocked: boolean; message?: string }> {
  const qs = new URLSearchParams();
  const userId = readUserId();
  if (userId) qs.set("suite_user_id", userId);
  const res = await fetch(`${suiteUrl("coffre-status.php")}${qs.toString() ? `?${qs}` : ""}`, {
    credentials: "include",
    cache: "no-store",
    headers: suiteHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    server_unlocked?: boolean | number | string;
    message?: string;
    error?: string;
  };
  const unlocked = data.server_unlocked === true || data.server_unlocked === 1 || data.server_unlocked === "1";
  const ok = res.ok && data.success !== false;
  return {
    ok,
    unlocked: ok && unlocked,
    message: data.message ?? data.error,
  };
}

export async function fetchCoffreList(
  path = "",
  zone: CoffreZone = "vault"
): Promise<{ path: string; entries: CoffreEntry[]; error?: string }> {
  const qs = new URLSearchParams();
  if (path) qs.set("path", path);
  qs.set("zone", zone);
  const res = await fetch(`${suiteUrl("coffre-list.php")}?${qs}`, {
    credentials: "include",
    cache: "no-store",
    headers: suiteHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    path?: string;
    entries?: Array<{ name: string; path: string; kind?: string; size?: number | null }>;
    message?: string;
    error?: string;
  };
  if (!res.ok || data.success === false) {
    return {
      path,
      entries: [],
      error: data.message ?? data.error ?? "Impossible de lire le Coffre.",
    };
  }
  const entries = (data.entries ?? [])
    .filter((e) => e.kind === "folder" || e.kind === "file")
    .map((e) => ({
      name: e.name,
      path: e.path,
      kind: (e.kind === "folder" ? "folder" : "file") as "folder" | "file",
      size: typeof e.size === "number" ? e.size : undefined,
    }));
  return { path: data.path ?? path, entries };
}

export async function fetchCoffreFile(
  path: string,
  zone: CoffreZone,
  fallbackName: string
): Promise<{ file: File | null; error?: string }> {
  const qs = new URLSearchParams();
  qs.set("path", path);
  qs.set("zone", zone);
  qs.set("stream", "1");
  try {
    const res = await fetch(`${suiteUrl("coffre-download.php")}?${qs}`, {
      credentials: "include",
      cache: "no-store",
      headers: suiteHeaders(),
    });
    if (!res.ok) {
      let message = `Erreur HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { message?: string };
        if (body.message) message = body.message;
      } catch {
        /* binaire */
      }
      return { file: null, error: message };
    }
    const blob = await res.blob();
    if (blob.size <= 0) {
      return { file: null, error: "Fichier Coffre vide. Réessayez ou téléversez-le." };
    }
    const name = fallbackName || path.split("/").pop() || "logo";
    return { file: new File([blob], name, { type: coffreLogoMime(name, blob.type) }) };
  } catch (e) {
    return { file: null, error: e instanceof Error ? e.message : "Téléchargement Coffre impossible." };
  }
}
